'use client';

/**
 * Settings ▸ Appearance — how the page looks: light or dark, and the reading font.
 *
 * Both used to live only in the header row. The moon/sun stays there too,
 * because flipping the theme is something people do on the spot; here it gains
 * the third answer the header button cannot give, "follow this device". The
 * font moved here outright — see FontPicker.tsx.
 *
 * Everything applies the moment it is chosen and is remembered on this device,
 * like the Flags tab: there is nothing to save.
 */

import { useTheme, type Theme } from '@/lib/theme';
import { FontList } from './FontPicker';

const THEMES: { id: Theme; label: string; hint: string; icon: JSX.Element }[] = [
  {
    id: 'light',
    label: 'Light',
    hint: 'Dark text on a light page',
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4"
              strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'dark',
    label: 'Dark',
    hint: 'Light text on a dark page',
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'system',
    label: 'Same as device',
    hint: 'Follows your system setting',
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"
           strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4.5" width="18" height="12" rx="2" />
        <path d="M8.5 20h7M12 16.5V20" />
      </svg>
    ),
  },
];

export default function AppearanceSettings() {
  const { theme, setTheme } = useTheme();

  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Light or dark</h3>
        <p className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">
          The moon and sun button in the header switches between light and dark too.
        </p>
        <div role="radiogroup" aria-label="Light or dark" className="mt-3 grid gap-2 sm:grid-cols-3">
          {THEMES.map((t) => {
            const on = theme === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setTheme(t.id)}
                className={
                  'flex items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition ' +
                  (on
                    ? 'border-accent-500 bg-accent-200 text-ink-900 dark:border-accent-500 dark:bg-accent-500/10 dark:text-ink-50'
                    : 'border-ink-200 text-ink-700 hover:border-ink-300 hover:bg-ink-50 ' +
                      'dark:border-ink-700 dark:text-ink-200 dark:hover:border-ink-600 dark:hover:bg-ink-800/60')
                }
              >
                <span className={on ? 'text-accent-600 dark:text-accent-400' : 'text-ink-400'}>{t.icon}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{t.label}</span>
                  <span className="block text-xs text-ink-500 dark:text-ink-400">{t.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Font</h3>
        <p className="mb-3 mt-0.5 text-xs text-ink-500 dark:text-ink-400">
          Applies to the whole page as soon as you pick it.
        </p>
        <FontList />
      </section>
    </div>
  );
}
